import React, { useImperativeHandle, useRef } from 'react';
import { Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import Icon from './Icon';

export interface PickedPhoto {
  /** Which shot this is, e.g. "Front"; extra photos are labelled "More". */
  label: string;
  url: string;
  /** Why the photo can't be used, e.g. "blurry". Highlights the tile. */
  issue?: string;
}

interface PhotoPickerProps {
  /** Shots the customer must add, in order, e.g. ["Front", "Back", "Tags & box"]. */
  required: string[];
  photos: PickedPhoto[];
  /** A new or replacement photo for `label`. */
  onPick: (label: string, file: File) => void;
  /** Allow extra photos after the required ones. */
  allowMore?: boolean;
  accept?: string;
  className?: string;
}

/** Lets a page open the file picker for a slot, e.g. a "Retake photo" button. */
export interface PhotoPickerHandle {
  open: (label: string) => void;
}

const tileBase = 'tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl';

/** Grid of labelled photo slots with an "Add photo" tile (Figma "Photos" card in R2 / R2E). */
const PhotoPicker = React.forwardRef<PhotoPickerHandle, PhotoPickerProps>(
  ({ required, photos, onPick, allowMore = true, accept = 'image/jpeg,image/png', className }, ref) => {
    const input = useRef<HTMLInputElement>(null);
    const target = useRef('');

    const open = (label: string) => {
      target.current = label;
      input.current?.click();
    };
    useImperativeHandle(ref, () => ({ open }));

    const extras = photos.filter((p) => !required.includes(p.label));
    const slots: { label: string; photo?: PickedPhoto }[] = [
      ...required.map((label) => ({
        label,
        photo: photos.find((p) => p.label === label),
      })),
      ...extras.map((photo) => ({ label: photo.label, photo })),
    ];
    const nextEmpty = slots.find((s) => !s.photo);

    return (
      <div className={cn('tw-flex tw-flex-wrap tw-gap-3', className)}>
        {slots.map(({ label, photo }, i) =>
          photo ? (
            <button
              key={`${label}-${i}`}
              type="button"
              onClick={() => open(label)}
              aria-label={photo.issue ? `Retake ${label} photo (${photo.issue})` : `Replace ${label} photo`}
              className="tw-group tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-xl focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
            >
              <span
                className={cn(
                  tileBase,
                  'tw-bg-ds-subtle',
                  photo.issue ? 'tw-border-[1.5px] tw-border-ds-danger-fg' : 'tw-border tw-border-transparent',
                )}
              >
                <img src={photo.url} alt="" className="tw-h-full tw-w-full tw-object-cover" />
              </span>
              <span
                className={cn('tw-text-[12px] tw-leading-4', photo.issue ? 'tw-font-semibold tw-text-ds-danger-fg' : 'tw-text-ds-text-secondary')}
              >
                {photo.issue ? `${label} · ${photo.issue}` : label}
              </span>
            </button>
          ) : (
            // Only the first empty slot is offered, so photos are added in order.
            label === nextEmpty?.label && <AddTile key={label} caption={label} onClick={() => open(label)} />
          ),
        )}
        {!nextEmpty && allowMore && <AddTile caption="Add more" onClick={() => open('More')} />}
        <input
          ref={input}
          type="file"
          accept={accept}
          className="tw-hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onPick(target.current, file);
            e.target.value = '';
          }}
        />
      </div>
    );
  },
);
PhotoPicker.displayName = 'PhotoPicker';

const AddTile: React.FC<{ caption: string; onClick: () => void }> = ({ caption, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-xl focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand"
  >
    <span
      className={cn(
        tileBase,
        'tw-flex-col tw-gap-1 tw-border-[1.5px] tw-border-dashed tw-border-ds-border-strong tw-text-ds-text-secondary hover:tw-bg-ds-subtle',
      )}
    >
      <Icon icon={Camera} size={18} />
      <span className="tw-text-[12px] tw-font-medium tw-leading-4">Add photo</span>
    </span>
    <span className="tw-text-[12px] tw-leading-4 tw-text-ds-text-secondary">{caption}</span>
  </button>
);

export default PhotoPicker;
